<template>
    <div class="share-to-patient">
        <b-button
            size="lg"
            variant="icon"
            :title="$translate('share_with_patient')"
            class="toggle-btn"
            @click="toggleDialog"
        >
            <share-with-patient-icon />
        </b-button>

        <b-form class="share-to-patient-dialog" :class="{ opened: shareToPatientDialogOpened }">
            <div class="triangle"></div>

            <div class="form-body">
                <div class="form-header">
                    <span class="title">{{ $translate('share_with_patient') }}</span>
                    <b-button variant="transparent" @click="toggleDialog">
                        <i class="fa fa-close" />
                    </b-button>
                </div>

                <div class="input-container">
                    <input
                        id="patient-email"
                        v-model="patientEmail"
                        :class="{ invalid: !isEmailValid && showEmailError }"
                        type="email"
                        required
                        :placeholder="emailPlaceholder"
                        @blur="showEmailError = true"
                    />
                </div>
                <div class="input-container">
                    <input
                        id="patient-dob"
                        v-model="patientDOB"
                        :class="{ invalid: !isDOBValid && showDOBError }"
                        type="text"
                        required
                        :placeholder="DOBPlaceholder"
                        @blur="showDOBError = true"
                    />
                </div>
            </div>

            <div
                v-if="!isFdaFriendly"
                class="fertilization"
                :class="{ selected: selectedShareType === SHARE_TYPE.FERTILIZATION_UPDATED }"
            >
                <b-checkbox
                    :class="{ disabled: selectedShareType === SHARE_TYPE.EMBRYO_CULTURE_UPDATED }"
                    :checked="selectedShareType === SHARE_TYPE.FERTILIZATION_UPDATED"
                    @change="changeSelectedShareType($event, SHARE_TYPE.FERTILIZATION_UPDATED)"
                >
                    {{ $translate(SHARE_TYPE.FERTILIZATION_UPDATED) }}
                </b-checkbox>
                <b-button
                    type="button"
                    class="pink-btn"
                    :class="{ active: isFormValid }"
                    :disabled="
                        showDemoDisclaimer || selectedShareType !== SHARE_TYPE.FERTILIZATION_UPDATED
                    "
                    @click="createEmail"
                >
                    {{ $translate('create_email') }}
                </b-button>
            </div>

            <div
                class="embryo-development"
                :class="{ selected: selectedShareType === SHARE_TYPE.EMBRYO_CULTURE_UPDATED }"
            >
                <div class="embryo-development-checkbox">
                    <b-checkbox
                        :class="{
                            disabled: selectedShareType === SHARE_TYPE.FERTILIZATION_UPDATED
                        }"
                        :checked="selectedShareType === SHARE_TYPE.EMBRYO_CULTURE_UPDATED"
                        @change="changeSelectedShareType($event, SHARE_TYPE.EMBRYO_CULTURE_UPDATED)"
                    >
                        {{ $translate(SHARE_TYPE.EMBRYO_CULTURE_UPDATED) }}
                        <span v-if="isSelectedWellInValid" class="embryo-development-error-message">
                            {{ $translate('you_need_to_select_wells_first') }}
                        </span>
                    </b-checkbox>
                    <b-button
                        type="button"
                        class="pink-btn"
                        :class="{ active: isEmbryoShareValid }"
                        :disabled="
                            showDemoDisclaimer ||
                            selectedShareType !== SHARE_TYPE.EMBRYO_CULTURE_UPDATED
                        "
                        @click="createEmail"
                    >
                        {{ $translate('create_email') }}
                    </b-button>
                </div>

                <div
                    v-if="selectedShareType === SHARE_TYPE.EMBRYO_CULTURE_UPDATED"
                    class="embryo-summary-container"
                >
                    <div class="select-all-checkbox">
                        <b-button
                            type="button"
                            class="pink-btn-outline"
                            :class="{ active: isEmbryoShareValid }"
                            @click="copyLink"
                        >
                            {{ $translate('copy_link') }}
                        </b-button>
                        <b-checkbox
                            class="pink-checkbox"
                            :checked="isAllWells"
                            @change="selectAllWells($event)"
                        >
                            {{ $translate('all_embryos') }}
                        </b-checkbox>
                    </div>

                    <div class="all-embryo-list">
                        <div
                            v-for="well in activeWells"
                            :key="well._id"
                            class="embryo-container"
                            :class="{ active: selectedWells.contains(well._id) }"
                            @click="checkUncheckWell(well._id)"
                        >
                            <b-checkbox
                                v-if="selectedWells.contains(well._id)"
                                class="embryo-checkbox pink-checkbox"
                                :checked="true"
                            ></b-checkbox>
                            <div class="latest-pic">
                                <img :src="well.lastImageUrl" :draggable="false" alt="" />
                                <well-status-overlay :well="well" />
                            </div>
                            <div class="embryo-detail">
                                <span>{{ well.slideEsId }}</span>
                                <span>E:id : {{ well.esId }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </b-form>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import UIUtils from '../../../business/utils/UIUtils'
import BackendProvider from '../../../business/services/BackendProvider'
import WellStatusOverlay from '../common/WellStatusOverlay'
import ShareWithPatientIcon from './icons/ShareWithPatientIcon'
import moment from 'moment'

export default {
    name: 'ShareToPatient',
    components: {
        ShareWithPatientIcon,
        WellStatusOverlay
    },
    data() {
        return {
            SHARE_TYPE: {
                FERTILIZATION_UPDATED: 'fertilization_update',
                EMBRYO_CULTURE_UPDATED: 'embryo_culture_update'
            },
            showEmailError: false,
            showDOBError: false,
            showWellError: false,
            patientEmail: '',
            patientDOB: '',
            cipher: '',
            appUrl: '',
            selectedWells: [],
            selectedShareType: null
        }
    },
    computed: {
        ...mapState({
            user: state => state.common.user,
            selectedTenant: state => state.common.selectedTenant,
            selectedPatient: state => state.timelapse.selectedPatient,
            selectedTreatment: state => state.timelapse.selectedTreatment,
            shareToPatientDialogOpened: state => state.timelapse.shareToPatientDialogOpened
        }),
        ...mapGetters({
            getPnCount: 'timelapse/getPnCount',
            showDemoDisclaimer: 'common/showDemoDisclaimer',
            isFdaFriendly: 'common/isFdaFriendly'
        }),
        isEmailValid() {
            return UIUtils.validateEmail(this.patientEmail)
        },
        isDOBValid() {
            return moment(this.patientDOB, 'YYYY', true).isValid() && !isNaN(this.patientDOB)
        },
        isFormValid() {
            return this.isEmailValid && this.isDOBValid
        },
        tenant() {
            if (this.selectedTenant._id) {
                return this.selectedTenant
            }

            const { tenants } = this.user
            return (tenants && tenants[0]) || {}
        },
        activeWells() {
            const { slides } = this.selectedTreatment
            return this.selectedTreatment.wellsByIndex
                .filter(well => !!well)
                .map(well => {
                    const esId = slides.find(slide => slide._id === well.slideId).esId
                    return { ...well, slideEsId: esId }
                })
        },
        wellsWith2Pn() {
            return this.activeWells.filter(well => this.getPnCount(well) === 2)
        },
        fertilizationUpdateHref() {
            const subject = `Fertilization status from ${this.tenant.name} (Powered by Fairtility)`
            const body = `Out of ${this.activeWells.length} mature eggs, ${this.wellsWith2Pn.length} were fertilized today.`

            return `mailto:${this.patientEmail}?subject=${subject}&body=${body}`
        },
        embryoDevelopmentUpdateEncodedLink() {
            return `${this.appUrl}/patient-share?${encodeURIComponent(this.cipher)}`
        },
        embryoDevelopmentUpdateLink() {
            return `${this.appUrl}/patient-share?${this.cipher}`
        },
        embryoDevelopmentUpdateHref() {
            const subject = `Embryo growth status from ${this.tenant.name} (Powered by Fairtility)`
            const intro = 'Click the link to see how your embryos are growing:'
            // while sending mail we use encode link
            const body = `${intro} ${this.embryoDevelopmentUpdateEncodedLink}`

            return `mailto:${this.patientEmail}?subject=${subject}&body=${body}`
        },
        hasAllBasicInfo() {
            return (
                this.isFormValid &&
                !!this.tenant &&
                !!this.selectedTreatment &&
                !!this.selectedPatient
            )
        },
        isAllWells() {
            return this.selectedWells.length === this.activeWells.length
        },
        emailPlaceholder() {
            return !this.isEmailValid && this.showEmailError
                ? this.$translate('enter_recipient_email_address')
                : this.$translate('email')
        },
        DOBPlaceholder() {
            return !this.isDOBValid && this.showDOBError
                ? this.$translate('enter_recipient_yob')
                : this.$translate('yob')
        },
        isSelectedWellInValid() {
            return (
                this.selectedShareType === this.SHARE_TYPE.EMBRYO_CULTURE_UPDATED &&
                this.showWellError &&
                !this.selectedWells.length
            )
        },
        isEmbryoShareValid() {
            return this.isFormValid && this.selectedWells.length
        }
    },
    methods: {
        toggleDialog() {
            if (!this.shareToPatientDialogOpened)
                this.$store.commit('timelapse/shareDialogOpened', false)

            this.resetData()

            this.$store.commit(
                'timelapse/shareToPatientDialogOpened',
                !this.shareToPatientDialogOpened
            )
        },
        checkUncheckWell(wellId) {
            this.showWellError = true
            const index = this.selectedWells.findIndex(id => id === wellId)
            if (index === -1) {
                this.selectedWells.push(wellId)
            } else {
                this.selectedWells.splice(index, 1)
            }
        },
        selectAllWells(selectAll) {
            this.showWellError = true
            if (selectAll) {
                this.selectedWells = this.activeWells.map(well => well._id)
            } else {
                this.selectedWells = []
            }
        },
        resetData() {
            this.showEmailError = false
            this.showDOBError = false
            this.showWellError = false
            this.selectedWells = []
            this.cipher = ''
            this.appUrl = ''
            this.patientEmail = ''
            this.patientDOB = ''
            this.selectedShareType = null
        },
        changeSelectedShareType(isCheck, type) {
            this.selectedShareType = isCheck ? type : null
        },
        async copyLink() {
            this.showEmailError = true
            this.showDOBError = true
            this.showWellError = true
            if (!this.hasAllBasicInfo || !this.selectedWells.length) return

            UIUtils.showLoadingMask()
            try {
                await this.createShareToPatientLink()
                // while coping to clipboard we use normal link (not encoded)
                await UIUtils.copyToClipboard(this.embryoDevelopmentUpdateLink)
            } catch (e) {}
            UIUtils.hideLoadingMask()
            this.toggleDialog()
        },
        async createEmail() {
            this.showEmailError = true
            this.showDOBError = true
            if (!this.hasAllBasicInfo) return

            if (this.selectedShareType === this.SHARE_TYPE.FERTILIZATION_UPDATED) {
                window.open(this.fertilizationUpdateHref)
                this.toggleDialog()
                return
            }
            if (this.selectedShareType === this.SHARE_TYPE.EMBRYO_CULTURE_UPDATED) {
                this.showWellError = true

                if (!this.selectedWells.length) return

                UIUtils.showLoadingMask()
                try {
                    await this.createShareToPatientLink()
                    window.open(this.embryoDevelopmentUpdateHref)
                } catch (e) {}
                UIUtils.hideLoadingMask()
                this.toggleDialog()
            }
        },
        async createShareToPatientLink() {
            const body = {
                patientId: this.selectedPatient._id,
                treatmentId: this.selectedTreatment._id,
                tenantId: this.tenant._id,
                email: this.patientEmail,
                birthYear: Number(this.patientDOB),
                wells: this.selectedWells
            }

            const { appUrl, cipher } = await BackendProvider.createShareToPatientUrl(body)
            this.appUrl = appUrl
            this.cipher = cipher
        }
    }
}
</script>

<style lang="scss" scoped>
.share-to-patient {
    .toggle-btn {
        height: 100%;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .share-to-patient-dialog {
        &.opened {
            position: absolute;
            color: $dark-4;
            border-top: solid 6px $pink;
            border-radius: 0 0 10px 10px;
            background-color: $light-2;

            .triangle {
                position: absolute;
                right: 50px;
                height: 0;
                border-top: 10px solid $pink;
                border-left: 10px solid transparent;
                border-right: 10px solid transparent;
            }
            .form-body {
                padding: 16px;

                .form-header {
                    margin: 10px 0;
                    display: flex;
                    justify-content: space-between;

                    .title {
                        font-weight: 600;
                    }
                    .fa-close {
                        font-size: 18px;
                        color: $dark-4;
                        // makes icon thinner
                        -webkit-text-stroke: 0.1rem $light-2;
                    }
                }
                .input-container {
                    width: 100%;
                    display: flex;
                    align-items: center;

                    &:last-child {
                        margin-top: 1rem;
                    }

                    input {
                        flex-grow: 1;
                        color: $dark-4;
                        border: none;
                        border-radius: 4px;
                        background-color: $light-3;

                        &:focus,
                        &.form-control:focus {
                            border: none;
                        }
                        &.invalid {
                            color: red;

                            &::placeholder {
                                color: red;
                            }
                        }
                    }
                    .next-btn {
                        margin-left: 10px;
                        width: 100px;
                        height: 38px;
                        font-size: 16px;
                        color: $light-3;

                        &:active {
                            background: $pink-gradient !important;
                        }

                        @media screen and #{$new-break5open} {
                            &:hover {
                                background: $pink-gradient !important;
                            }
                        }
                    }
                }
            }

            .fertilization,
            .embryo-development {
                &.selected {
                    background-color: $white;
                }
            }
            .fertilization {
                padding: 16px;
                display: flex;
                justify-content: space-between;
                align-items: center;
                border-top: solid 1px $dark-4;
            }
            .embryo-development {
                padding: 16px;
                display: flex;
                flex-direction: column;
                border-top: solid 1px $dark-4;
                position: relative;

                .embryo-development-checkbox {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }
                .embryo-summary-container {
                    width: 100%;
                    white-space: normal;
                    padding: 0.5rem;

                    .all-embryo-list {
                        display: flex;
                        gap: 0.75rem;
                        margin-top: 0.5rem;
                        flex-wrap: wrap;
                        max-height: 15rem;
                        overflow-y: auto;
                        @include hide-scrollbar();

                        .embryo-container {
                            padding: 0.5rem;
                            cursor: pointer;

                            &:not(.active) {
                                background-color: $light-alpha;
                            }
                            &.active {
                                background-color: $pink-alpha;
                            }
                            .latest-pic {
                                position: relative;
                                width: 3.5rem;

                                img {
                                    height: 3.5rem;
                                    width: 3.5rem;
                                    border-radius: $border-radius;
                                }
                            }
                            .embryo-detail {
                                display: flex;
                                flex-direction: column;
                                padding-top: 0.5rem;

                                font-size: 1rem;
                                color: $dark-3;
                            }
                        }
                    }
                }
            }
            .actions {
                width: 100%;
                display: flex;
                justify-content: end;

                a {
                    color: inherit;
                    text-decoration: none;
                }
            }
            .pink-btn {
                height: 38px;
                font-size: 16px;
                color: $light-3;

                &.active:not(:disabled) {
                    background: $pink-gradient !important;
                }
            }

            .pink-btn-outline {
                --main-color: #{$dark};
                height: 30px;
                font-size: 10px;
                color: var(--main-color) !important;
                border: 1px solid var(--main-color) !important;
                border-radius: 19px;
                font-weight: bold !important;
                background-color: transparent !important;
                &:active,
                &:hover {
                    color: var(--main-color) !important;
                }

                &.active {
                    --main-color: #{$pink};
                }
            }

            @media screen and #{$new-break4close} {
                width: 350px;
                top: 0;
                right: 40px;
                font-size: 12px;

                input {
                    font-size: 12px;
                    padding: 15px;
                }
            }
            @media screen and #{$new-break5open} {
                width: 430px;
                top: 50px;
                right: 15px;
                font-size: 15px;

                input {
                    font-size: 14px;
                    padding: 0.75rem;
                }
            }
        }
        &:not(.opened) {
            display: none;
        }
    }
}
</style>

<style lang="scss">
.share-to-patient {
    input:-webkit-autofill,
    input:-webkit-autofill:hover,
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active {
        -webkit-box-shadow: 0 0 0 30px $light-3 inset !important;
    }

    .custom-control-label {
        cursor: pointer;
    }
    .fertilization {
        .custom-control-label {
            &::after,
            &::before {
                top: 0.30313rem;
            }
        }
    }
    .embryo-development {
        .embryo-development-error-message {
            position: absolute;
            left: 0;
            top: 1.5rem;
            font-size: 0.8rem;
            color: red;
        }
        .select-embryos-container {
            margin-top: 0.5rem;

            .custom-radio {
                color: $dark-4;

                .custom-control-input:focus ~ .custom-control-label::before {
                    box-shadow: none;
                }
                .custom-control-label::before {
                    background-color: $light-alpha;
                }
                .custom-control-input:checked ~ .custom-control-label::before {
                    background-color: $pink;
                    border-color: $pink;
                }
                .custom-control-label {
                    &::before,
                    &::after {
                        border: none;
                        @media screen and #{$new-break4close} {
                            top: 0.15rem;
                            left: -1.4rem;
                        }
                        @media screen and #{$new-break5open} {
                            top: 0.3rem;
                            left: -1.4rem;
                        }
                    }
                }
            }
        }
        .select-all-checkbox {
            display: flex;
            justify-content: space-between;
        }
        .embryo-container {
            position: relative;
            .embryo-checkbox {
                position: absolute;
                top: 0.3rem;
                right: -0.2rem;
            }
        }
        .pink-checkbox {
            .custom-control-label {
                cursor: pointer;
            }
            .custom-control-label::before {
                box-shadow: none;
                border-style: none;
                border-radius: 50%;
            }
            .custom-control-input:not(:disabled):active ~ .custom-control-label::before {
                background-color: $pink;
                opacity: 0.3;
            }
            .custom-control-input:checked ~ .custom-control-label::before {
                background-color: $pink;
            }
        }
    }
}
</style>
